/**
 * Aircraft Type Autocomplete Hook
 *
 * Provides debounced aircraft type search functionality for autocomplete dropdowns
 */

import { useState, useEffect, useRef } from 'react';
import { useLazyAutocompleteAircraftTypesQuery } from '../services/redux/api/aircraftType.api';
import type { AircraftTypeAutocomplete } from '../services/redux/api/aircraftType.api';

interface UseAircraftTypeAutocompleteOptions {
  delay?: number;
  minQueryLength?: number;
  limit?: number;
}

interface UseAircraftTypeAutocompleteReturn {
  aircraftTypes: AircraftTypeAutocomplete[];
  loading: boolean;
  error: string | null;
  search: (query: string) => void;
}

export function useAircraftTypeAutocomplete(
  options: UseAircraftTypeAutocompleteOptions = {}
): UseAircraftTypeAutocompleteReturn {
  const { delay = 300, minQueryLength = 1, limit = 10 } = options;

  const [aircraftTypes, setAircraftTypes] = useState<AircraftTypeAutocomplete[]>([]);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [trigger, { data, isLoading, error: apiError }] = useLazyAutocompleteAircraftTypesQuery();

  const search = (searchQuery: string) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (!searchQuery || searchQuery.length < minQueryLength) {
      setAircraftTypes([]);
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      trigger({ query: searchQuery, limit });
    }, delay);
  };

  useEffect(() => {
    if (data) {
      setAircraftTypes(data);
    }
  }, [data]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return {
    aircraftTypes,
    loading: isLoading,
    error: apiError ? 'Failed to fetch aircraft types' : null,
    search,
  };
}
